import axiosInstance from "../api/axiosInstance";
import { Live, LiveFilters, Country, Spot, MapSpot } from "@/common/model/Live";

export type { Spot };

export const LiveService = {
  async getLiveSessions(
    page: number,
    accesstoken: string,
    filters?: LiveFilters
  ): Promise<Live[]> {
    try {
      const params = new URLSearchParams();
      params.append("accesstoken", accesstoken);

      if (filters?.countryId) {
        params.append("countryid", filters.countryId.toString());
      }
      if (filters?.spotId) {
        params.append("spotid", filters.spotId.toString());
      }
      if (filters?.gender && filters.gender !== "all") {
        params.append("gender", filters.gender);
      }
      if (filters?.following) {
        params.append("following", "true");
      }
      if (filters?.sort) {
        params.append("sort", filters.sort);
      }
      if (filters?.sortDir) {
        params.append("sortdir", filters.sortDir);
      }

      const response = await axiosInstance.get(
        `/live/list/${page}?${params.toString()}`,
        { skipLoading: true }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching live sessions:", error);
      return [];
    }
  },

  async getAllLiveSessions(
    accesstoken: string,
    filters?: LiveFilters
  ): Promise<Live[]> {
    // Fetch multiple pages to get all live sessions
    const allSessions: Live[] = [];
    let page = 0;
    let hasMore = true;

    while (hasMore) {
      const sessions = await this.getLiveSessions(page, accesstoken, filters);
      if (sessions.length === 0) {
        hasMore = false;
      } else {
        allSessions.push(...sessions);
        page++;
        // Limit to prevent infinite loops (max 500 sessions = 25 pages)
        if (page >= 25) hasMore = false;
      }
    }

    return allSessions;
  },

  async getCountries(accesstoken: string): Promise<Country[]> {
    try {
      // Fetch all countries (using high limit to get all at once)
      const response = await axiosInstance.get(
        `/countries/getcountries/300/0?accesstoken=${accesstoken}`,
        { skipLoading: true }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      console.log("Countries loaded:", response.data?.length || 0);
      return response.data;
    } catch (error) {
      console.error("Error fetching countries:", error);
      return [];
    }
  },

  async searchSpots(
    query: string,
    accesstoken: string,
    countryId?: number
  ): Promise<Spot[]> {
    try {
      let url = `/kiteworldmap/spots/query?filter=${encodeURIComponent(
        query
      )}&limit=20&accesstoken=${accesstoken}`;

      if (countryId) {
        url += `&countryid=${countryId}`;
      }

      const response = await axiosInstance.get(url, { skipLoading: true });

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error searching spots:", error);
      return [];
    }
  },

  async getSpotStatistics(
    spotId: number,
    accesstoken: string
  ): Promise<{ totalRiders: number; kiteSizes: Record<number, number> }> {
    try {
      const response = await axiosInstance.get(
        `/live/spots/statistics/${spotId}?accesstoken=${accesstoken}`,
        { skipLoading: true }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching spot statistics:", error);
      return { totalRiders: 0, kiteSizes: {} };
    }
  },

  async getLiveSessionById(
    sessionId: number,
    accesstoken: string
  ): Promise<Live | null> {
    try {
      const response = await axiosInstance.get(
        `/live/${sessionId}?accesstoken=${accesstoken}`,
        { skipLoading: true }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching live session:", error);
      return null;
    }
  },

  async getSpotsForMap(
    lat: number,
    lon: number,
    latDelta: number,
    lonDelta: number,
    maxSpotsShown: number = 200
  ): Promise<MapSpot[]> {
    try {
      const params = new URLSearchParams();
      params.append("lat", lat.toString());
      params.append("lon", lon.toString());
      params.append("latDelta", latDelta.toString());
      params.append("lonDelta", lonDelta.toString());
      params.append("maxSpotsShown", maxSpotsShown.toString());

      const response = await axiosInstance.get(
        `/spots/findspots?${params.toString()}`,
        { skipLoading: true }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching spots for map:", error);
      return [];
    }
  },

  /**
   * Admin spot search - simple search without extra joins
   * Returns all spots matching the query, including those without windguru data
   */
  async adminSearchSpots(
    query: string,
    limit: number = 20
  ): Promise<AdminSpotSearchResult[]> {
    try {
      const response = await axiosInstance.get(
        `/admin/spots/search?q=${encodeURIComponent(query)}&limit=${limit}`,
        { skipLoading: true }
      );

      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return response.data;
    } catch (error) {
      console.error("Error in admin spot search:", error);
      return [];
    }
  },
};

export interface AdminSpotSearchResult {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  countryId: number;
  countryName: string | null;
  alpha2: string | null;
}
